/**
 * database.ts — Comlink proxy to a dedicated Web Worker running SQLite.
 *
 * The Worker uses wa-sqlite's sync build (no Asyncify) with
 * AccessHandlePoolVFS (OPFS). This eliminates the memory overhead that
 * caused iOS WebKit OOM crashes.
 *
 * The public API is identical to the previous direct wa-sqlite wrapper.
 * All 28+ consumer files see no change.
 *
 * Public API:
 *   db.exec(sql, params?)      → Promise<any[][]>
 *   db.execO<T>(sql, params?)  → Promise<T[]>
 *   db.execMany(queries)       → Promise<void>
 *   db.prepare(sql)            → Promise<PreparedStatement>
 *   db.tx(fn)                  → Promise<T>
 *   db.onUpdate(callback)      → () => void
 *   db.close()                 → Promise<void>
 */

import * as Comlink from 'comlink'
import type { WorkerApi } from './db-worker'

// ---------------------------------------------------------------------------
// Types (unchanged from previous version)
// ---------------------------------------------------------------------------

export type SQLiteCompatibleType =
  | number
  | string
  | Uint8Array
  | null
  | bigint
  | boolean
  | undefined

/** Simplified update listener — what consumers subscribe to (no args). */
type UpdateListener = () => void

export interface PreparedStatement {
  bind(params: any[]): PreparedStatement
  run(): Promise<void>
}

export interface Database {
  /** Execute SQL returning positional rows (array of arrays). */
  exec(sql: string, params?: any[]): Promise<any>

  /** Execute SQL returning object rows (array of {col: value}). */
  execO<T = Record<string, any>>(sql: string, params?: any[]): Promise<T[]>

  /** Execute many SQL statements (no params). */
  execMany(queries: string[]): Promise<void>

  /** Prepare a statement for repeated execution with different bindings. */
  prepare(sql: string): Promise<PreparedStatement>

  /**
   * Run a function inside a transaction.
   * The callback receives a transaction object with its own `exec` method.
   */
  tx<T = void>(fn: (tx: TransactionContext) => Promise<T>): Promise<T>

  /** Subscribe to any database mutation. Returns an unsubscribe function. */
  onUpdate(callback: UpdateListener): () => void

  /** Close the database. */
  close(): Promise<void>
}

export interface TransactionContext {
  exec(sql: string, params?: any[]): Promise<any>
}

// ---------------------------------------------------------------------------
// Worker management
// ---------------------------------------------------------------------------

let worker: Worker | null = null
let workerProxy: Comlink.Remote<WorkerApi> | null = null

function getWorker(): { worker: Worker; proxy: Comlink.Remote<WorkerApi> } {
  if (!worker || !workerProxy) {
    worker = new Worker(new URL('./db-worker.ts', import.meta.url), {
      type: 'module',
    })
    workerProxy = Comlink.wrap<WorkerApi>(worker)
  }
  return { worker, proxy: workerProxy }
}

/**
 * Terminate the Worker and reset state.
 *
 * Replaces the old `resetWasmModule()`. Used by `db-context.tsx` in the
 * corruption recovery path: terminate → clearDatabase → respawn.
 */
export function terminateWorker(): void {
  if (worker) {
    worker.terminate()
    worker = null
    workerProxy = null
  }
}

/**
 * Clear all OPFS data for a database (corruption recovery).
 * This must be called after `terminateWorker()` so the VFS releases
 * its access handles. A new Worker is spawned to perform the cleanup.
 */
export async function clearDatabase(dbName: string): Promise<void> {
  const { proxy } = getWorker()
  await proxy.clearDatabase(dbName)
}

// ---------------------------------------------------------------------------
// Core implementation
// ---------------------------------------------------------------------------

/**
 * Open (or create) a named SQLite database backed by OPFS.
 *
 * @param dbName  Logical name for the database (maps to an OPFS directory).
 * @param schema  Optional SQL to run on open (CREATE TABLE IF NOT EXISTS …).
 */
export async function openDatabase(
  dbName: string,
  schema?: string
): Promise<Database> {
  const { proxy } = getWorker()

  // Open the database in the Worker
  await proxy.openDatabase(dbName, schema)

  // ---------------------------------------------------------------------------
  // Update listeners — subscribe to BroadcastChannel from the Worker
  // ---------------------------------------------------------------------------
  const listeners = new Set<UpdateListener>()
  const channelName = await proxy.getUpdateChannelName(dbName)
  const channel = new BroadcastChannel(channelName)

  channel.onmessage = (event) => {
    console.log('[database] BroadcastChannel received message:', event.data, 'listeners:', listeners.size)
    listeners.forEach((fn) => {
      try {
        fn()
      } catch (err) {
        console.warn('[database] Update listener threw error:', err)
      }
    })
  }

  // ---------------------------------------------------------------------------
  // Public API (identical interface to the old direct wrapper)
  // ---------------------------------------------------------------------------

  const database: Database = {
    async exec(sql: string, params?: any[]): Promise<any[][]> {
      return proxy.exec(dbName, sql, params)
    },

    async execO<T = Record<string, any>>(
      sql: string,
      params?: any[]
    ): Promise<T[]> {
      return proxy.execO(dbName, sql, params) as Promise<T[]>
    },

    async execMany(queries: string[]): Promise<void> {
      return proxy.execMany(dbName, queries)
    },

    async prepare(sql: string): Promise<PreparedStatement> {
      // Same approach as before: store SQL and replay for each bind/run cycle.
      let currentParams: any[] = []

      const stmt: PreparedStatement = {
        bind(params: any[]): PreparedStatement {
          currentParams = params
          return stmt
        },
        async run(): Promise<void> {
          await proxy.exec(dbName, sql, currentParams)
        },
      }

      return stmt
    },

    async tx<T = void>(fn: (tx: TransactionContext) => Promise<T>): Promise<T> {
      await proxy.beginTransaction(dbName)
      try {
        const txCtx: TransactionContext = {
          exec(sql: string, params?: any[]): Promise<any[][]> {
            return proxy.exec(dbName, sql, params)
          },
        }
        const result = await fn(txCtx)
        await proxy.commitTransaction(dbName)
        return result
      } catch (err) {
        await proxy.rollbackTransaction(dbName)
        throw err
      }
    },

    onUpdate(callback: UpdateListener): () => void {
      listeners.add(callback)
      return () => {
        listeners.delete(callback)
      }
    },

    close(): Promise<void> {
      listeners.clear()
      channel.close()
      return proxy.close(dbName)
    },
  }

  return database
}
